"use client";

import React from "react";
import Link from "next/link";
import { Menu } from "lucide-react";
import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton } from "@clerk/nextjs";

import { routeList } from "@/@data/navbar";
import Logo from "@/components/layout/logo";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <header className="fixed top-0 z-50 w-full bg-[#0f172a] border-b border-slate-800">
      <div className="max-w-[1440px] mx-auto px-10 flex items-center justify-between h-16">
        {/* Logo */}
        <Logo />

        {/* Desktop nav links */}
        <nav className="hidden lg:flex items-center gap-8">
          {routeList.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Desktop auth buttons */}
        <div className="hidden lg:flex items-center gap-3">
          <SignedOut>
            <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
              <button className="text-sm font-medium text-slate-400 hover:text-white transition-colors px-4 py-2">
                Log in
              </button>
            </SignInButton>
            <SignUpButton mode="modal" fallbackRedirectUrl="/subscribe">
              <button className="bg-[#F97316] text-white px-6 py-2 rounded text-sm font-bold uppercase tracking-wider hover:opacity-90 transition-opacity">
                Get Started
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <Link href="/dashboard">
              <button className="text-sm font-medium text-slate-400 hover:text-white transition-colors px-4 py-2">
                Dashboard
              </button>
            </Link>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </div>

        {/* Mobile hamburger */}
        <div className="flex items-center lg:hidden">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-slate-400 hover:text-white"
              >
                <Menu className="size-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-white flex flex-col justify-between">
              <div>
                <SheetHeader className="mb-6 ml-4">
                  <SheetTitle className="flex items-center">
                    <Logo />
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-2">
                  {routeList.map(({ href, label }) => (
                    <Button
                      key={href}
                      onClick={() => setIsOpen(false)}
                      asChild
                      variant="ghost"
                      className="justify-start text-base"
                    >
                      <Link href={href}>{label}</Link>
                    </Button>
                  ))}
                  <SignedIn>
                    <Separator className="my-2" />
                    <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Platform
                    </div>
                    {[
                      { href: "/dashboard", label: "Dashboard" },
                      { href: "/dashboard/rental-map", label: "Rental Map" },
                      { href: "/dashboard/comps", label: "Comps Tool" },
                      { href: "/dashboard/vacancy", label: "Vacancy Map" },
                      { href: "/dashboard/reports", label: "Reports" },
                    ].map(({ href, label }) => (
                      <Button
                        key={href}
                        onClick={() => setIsOpen(false)}
                        asChild
                        variant="ghost"
                        className="justify-start text-base"
                      >
                        <Link href={href}>{label}</Link>
                      </Button>
                    ))}
                  </SignedIn>
                </div>
              </div>
              <div className="p-4">
                <Separator className="mb-4" />
                <SignedOut>
                  <SignUpButton mode="modal" fallbackRedirectUrl="/subscribe">
                    <button className="w-full bg-[#F97316] text-white py-3 rounded text-sm font-bold uppercase tracking-wider hover:opacity-90 transition-opacity">
                      Get Started
                    </button>
                  </SignUpButton>
                </SignedOut>
                <SignedIn>
                  <div className="flex items-center gap-3">
                    <UserButton afterSignOutUrl="/" />
                    <span className="text-sm text-slate-600">Account</span>
                  </div>
                </SignedIn>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
